import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface Props {
  label: string;
  ariaLabel?: string;
  placeholder?: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  maxLength?: number;
}

const DEFAULT_MAX = 2000;

export function HomeworkTextarea({
  label,
  ariaLabel,
  placeholder,
  value,
  onChange,
  disabled = false,
  maxLength = DEFAULT_MAX,
}: Props) {
  const current = value ?? '';
  const charCount = current.length;
  const ratio = charCount / maxLength;
  const overLimit = charCount > maxLength;
  // Counter is only visually loud when it starts to matter — under 50% it
  // fades into the chrome so the practitioner's eye stays on the prose.
  const counterTone = overLimit
    ? 'text-error-500'
    : ratio > 0.85
      ? 'text-warning-500'
      : ratio > 0.5
        ? 'text-neutral-500'
        : 'text-neutral-300';

  return (
    <div className="flex flex-col gap-1.5">
      {label !== '' ? (
        <span className="text-accent-ink text-[13px] font-medium">{label}</span>
      ) : null}
      <Textarea
        value={current}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        disabled={disabled}
        rows={3}
        aria-label={ariaLabel ?? (label !== '' ? label : undefined)}
        // Body text stays at the regular reading size; placeholder shrinks
        // and tones down so the empty state reads like a hint, not a header.
        // `placeholder:font-normal` overrides the textarea base in case the
        // field's italic/medium leak through.
        className="border-accent-ink/15 focus-visible:border-primary-300 resize-y text-[13.5px] leading-relaxed placeholder:text-[12.5px] placeholder:leading-snug placeholder:font-normal placeholder:text-neutral-400"
      />
      <p
        className={cn(
          'self-end font-mono text-[10.5px] tabular-nums transition-colors',
          counterTone,
        )}
      >
        {`${String(charCount)} / ${String(maxLength)}`}
      </p>
    </div>
  );
}
